import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Menu, X, Globe, CalendarCheck } from 'lucide-react';

const navItems = [
  { key: 'mission',      id: 'mission' },
  { key: 'programs',     id: 'programs' },
  { key: 'testimonials', id: 'testimonials' },
  { key: 'healing',      id: 'crystal-healing' },
  { key: 'about',        id: 'about' },
  { key: 'connect',      id: 'connect' },
];

const CORE_SECTION_SLUGS = new Set([
  'home', 'mission', 'programs', 'challenges', 'testimonials', 'healing',
  'crystal-healing', 'about', 'connect', 'book', 'challenge',
  'download', 'admin'
]);

const Header = ({ onRegisterClick }) => {
  const { t, toggleLanguage, isRTL } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [cmsPages, setCmsPages] = useState([]);

  useEffect(() => {
    const fetchCmsPages = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/cms/pages`);
        if (response.ok) {
          const pages = await response.json();
          setCmsPages(pages.filter(p => !CORE_SECTION_SLUGS.has(p.slug)));
        }
      } catch (err) {
        console.error('Header: error fetching CMS pages', err);
      }
    };
    fetchCmsPages();
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMenuOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
    );
    navItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // When navigating from a sub-page to /#section, scroll after the home page mounts
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const sectionId = location.hash.slice(1);
      const timer = setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash]);

  const navigateToSection = (sectionId) => {
    setIsMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-10 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-charcoal/98 backdrop-blur-md shadow-lg shadow-black/20'
            : 'bg-transparent'
        }`}
      >
        <nav className="container mx-auto px-4 sm:px-6 lg:px-10">
          {/* Top bar: CTA | Logo | Controls */}
          <div className="flex items-center justify-between h-16 sm:h-20 lg:h-24">

            {/* Left — Book CTA (desktop/tablet) */}
            <div className="flex items-center">
              <button
                onClick={onRegisterClick}
                className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 bg-gold text-charcoal text-xs uppercase tracking-wider font-semibold hover:bg-gold-500 transition-colors rounded-lg"
              >
                <CalendarCheck size={15} className="text-charcoal" />
                <span>{isRTL ? 'سجل الآن' : 'Book a Call'}</span>
              </button>
            </div>

            {/* Center — Logo + Tagline */}
            <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-0.5">
              <Link
                to="/"
                onClick={handleLogoClick}
                className="block"
              >
                <span className="font-script text-xl sm:text-2xl lg:text-3xl text-gold tracking-wide whitespace-nowrap">
                  {t.brand.name}
                </span>
              </Link>
              <p className="hidden lg:block text-[9px] uppercase tracking-[0.2em] text-cream/55 font-medium whitespace-nowrap">
                {t.brand.tagline}
              </p>
            </div>

            {/* Right — Language toggle + Mobile hamburger */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 text-gold hover:text-gold-500 transition-colors p-2"
                aria-label="Toggle language"
              >
                <Globe size={17} className="text-gold" />
                <span className="text-xs uppercase tracking-wider hidden sm:inline text-gold">
                  {t.nav.languageToggle}
                </span>
              </button>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 text-gold hover:text-gold-500 transition-colors"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Desktop Navigation — below top bar */}
          <div className="hidden lg:flex items-center justify-center gap-10 pb-5 border-b border-gold/10">
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.key}
                  onClick={() => navigateToSection(item.id)}
                  className={`nav-link transition-colors relative group ${
                    isActive
                      ? 'text-gold'
                      : 'text-cream-300 hover:text-gold'
                  }`}
                >
                  {t.nav[item.key]}
                  {/* Active dot indicator */}
                  <span
                    className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gold transition-opacity duration-300 ${
                      isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-30'
                    }`}
                  />
                </button>
              );
            })}
            {/* Challenges — standalone page link */}
            <Link
              to="/challenges"
              className={`nav-link transition-colors relative group ${
                location.pathname === '/challenges' ? 'text-gold' : 'text-cream-300 hover:text-gold'
              }`}
            >
              {t.nav.challenges}
              <span
                className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gold transition-opacity duration-300 ${
                  location.pathname === '/challenges' ? 'opacity-100' : 'opacity-0 group-hover:opacity-30'
                }`}
              />
            </Link>
            {cmsPages.map((page) => (
              <Link
                key={page.slug}
                to={`/${page.slug}`}
                className="nav-link text-cream-300 hover:text-gold transition-colors relative group"
              >
                {isRTL ? (page.title_ar || page.title) : page.title}
              </Link>
            ))}
          </div>
        </nav>
      </header>

      {/* Mobile Navigation Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-all duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-charcoal/97 backdrop-blur-md"
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu Content */}
        <div
          className={`relative h-full flex flex-col pt-20 transition-transform duration-300 ${
            isMenuOpen ? 'translate-y-0' : '-translate-y-4'
          }`}
        >
          <div className="flex-1 overflow-y-auto py-4">
            {navItems.map((item, index) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.key}
                  onClick={() => navigateToSection(item.id)}
                  style={{ animationDelay: `${index * 40}ms` }}
                  className={`w-full py-4 px-6 text-start transition-all uppercase tracking-wider text-sm border-b border-white/5 flex items-center justify-between ${
                    isActive
                      ? 'text-gold bg-gold/5'
                      : 'text-cream hover:text-gold hover:bg-white/5'
                  }`}
                >
                  <span>{t.nav[item.key]}</span>
                  {isActive && (
                    <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  )}
                </button>
              );
            })}
            {/* Challenges — standalone page */}
            <Link
              to="/challenges"
              onClick={() => setIsMenuOpen(false)}
              style={{ animationDelay: `${navItems.length * 40}ms` }}
              className={`w-full py-4 px-6 text-start transition-all uppercase tracking-wider text-sm border-b border-white/5 flex items-center justify-between ${
                location.pathname === '/challenges' ? 'text-gold bg-gold/5' : 'text-cream hover:text-gold hover:bg-white/5'
              }`}
            >
              <span>{t.nav.challenges}</span>
              {location.pathname === '/challenges' && (
                <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
              )}
            </Link>
            {cmsPages.map((page, index) => (
              <Link
                key={page.slug}
                to={`/${page.slug}`}
                onClick={() => setIsMenuOpen(false)}
                style={{ animationDelay: `${(navItems.length + 1 + index) * 40}ms` }}
                className="w-full py-4 px-6 text-start transition-all uppercase tracking-wider text-sm border-b border-white/5 flex items-center text-cream hover:text-gold hover:bg-white/5"
              >
                {isRTL ? (page.title_ar || page.title) : page.title}
              </Link>
            ))}
          </div>

          {/* Mobile CTA — fixed at bottom */}
          <div className="p-4 border-t border-gold/10 bg-charcoal/95 safe-area-bottom">
            <button
              onClick={() => { onRegisterClick && onRegisterClick(); setIsMenuOpen(false); }}
              className="w-full py-4 bg-gold text-charcoal text-sm uppercase tracking-wider font-semibold flex items-center justify-center gap-2 active:scale-[0.98] rounded-lg"
            >
              <CalendarCheck size={17} className="text-charcoal" />
              <span>{isRTL ? 'سجل الآن' : 'Book a Free Call'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
